import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'class-names';

import { SVGIcon } from '@uikit/old/Icon/Icon';
import Button from '@uikit/Form/Button/Button';
import Loader from '@uikit/old/Loader/Loader';
import Radio from '@uikit/old/Radio/Radio';
import RightInfoSidebar from '@uikit/old/RightInfoSidebar/RightInfoSidebar';

import PageHeader from '@uikit/old/PageHeader/PageHeader';
import BillingFormField from '@components/Publisher/Settings/SettingsBillingDetails/_components/BillingFormField/BillingFormField';
import BankAccountForm, {
  BankAccountFormValue,
} from '@components/Publisher/Settings/SettingsBillingDetails/_components/BankAccountForm/BankAccountForm';
import PayPalAccountForm, {
  PayPalAccountFormValue,
} from '@components/Publisher/Settings/SettingsBillingDetails/_components/PayPalAccountForm/PayPalAccountForm';
import WiseAccountForm, {
  WiseAccountFormValue,
} from '@components/Publisher/Settings/SettingsBillingDetails/_components/WiseAccountForm/WiseAccountForm';
import PayoutMethodCard from '@components/Publisher/Settings/SettingsBillingDetails/_components/PayoutMethodCard/PayoutMethodCard';
import Select from '@uikit/Form/Select/Select';
import { countries } from '@constants/countries.contstants';
import { isUsCountry, US_STATES } from '@constants/usStates.constants';

import {
  BillingEntityClassification,
  BillingEntityClassificationMap,
  BillingPublisherPaymentMethod,
  BillingPublisherPaymentMethodMap,
  BillingPublisherPayoutProfileType,
} from 'respona_api/generated/common_pb';
import Input from '@uikit/Form/Input/Input';
import { StateType } from '@redux/reducers';
import {
  BillingPublisherAddressType,
  BillingPublisherPaymentPayPalSettingsType,
  BillingPublisherPaymentWiseAccountSettingsType,
  BillingPublisherPayoutSettingsType,
  PayoutFieldErrorType,
} from '@ts/billing-publisher.types';
import {
  fetchBillingAddress,
  fetchPublisherBillingDetails,
  removePublisherPayoutSettings,
  resendPublisherPayoutValidationCode,
  updatePublisherBillingAddress,
  updatePublisherPayoutSettings,
  verifyPublisherPayoutValidationCode,
} from '@redux/thunks/billings-publisher.thunks';
import { addNotification } from '@redux/actions/notifications.actions';
import { userProfileSelector } from '@redux/selectors/userProfile.selector';
import { parseApiErrorMessage } from '@utils/errorUtils';
import { isFeatureEnabled } from '@utils/featureFlags';
import loadingStatuses from '@constants/loadingStatuses';
import {
  PAYOUT_MAX,
  validateBillingProfile,
} from '@components/Publisher/Settings/SettingsBillingDetails/_components/payout-validation';
import { getCountryTaxInfo } from '@components/Publisher/Settings/SettingsBillingDetails/_components/tax-info-by-country';
import { LEGAL_TYPES } from '@components/Publisher/Settings/SettingsBillingDetails/_components/BankAccountForm/constants';

import './SettingsBillingDetails.scss';
import './_components/PayoutMethodCard/PayoutMethodCard.scss';

const directDepositImg = require('@assets/direct-deposit.png').default;
const payPalImg = require('@assets/pay-pal-blue.png').default;
const wiseImg = require('@assets/wise.png').default;

type PaymentMethodValue = BillingPublisherPaymentMethodMap[keyof BillingPublisherPaymentMethodMap];
type EntityClassificationValue =
  BillingEntityClassificationMap[keyof BillingEntityClassificationMap];

type PaymentMethodOption = {
  key: string;
  label: string;
  descLineOne: string;
  descLineTwo: string;
  value: PaymentMethodValue | number;
  isComingSoon: boolean;
};

// Used as native `<input type=date>` `min`/`max` bounds so the OS date picker
// guides the publisher to a valid range (18+, under 120). The validator is
// still the source of truth — these just save the user a wrong-pick step.
const today = new Date();
const isoOfDateNYearsAgo = (n: number): string => {
  const d = new Date(today.getFullYear() - n, today.getMonth(), today.getDate());
  return d.toISOString().split('T')[0];
};
const DOB_MAX_ISO = isoOfDateNYearsAgo(18);
const DOB_MIN_ISO = isoOfDateNYearsAgo(120);

const ENTITY_CLASSIFICATION_OPTIONS: { value: EntityClassificationValue; label: string }[] = [
  { value: BillingEntityClassification.CORPORATION, label: 'Corporation' },
  { value: BillingEntityClassification.PARTNERSHIP, label: 'Partnership' },
  { value: BillingEntityClassification.TRUST, label: 'Trust' },
  { value: BillingEntityClassification.DISREGARDED_ENTITY, label: 'Disregarded entity' },
];

const paymentMethods: PaymentMethodOption[] = [
  {
    key: 'paypal',
    label: 'PayPal',
    descLineOne: 'Delivery: 6-10 business days',
    descLineTwo: 'Processing fee: 2% of the withdrawal',
    value: BillingPublisherPaymentMethod.PAYPAL,
    isComingSoon: false,
  },
  {
    key: 'wiseAccount',
    label: 'Wise',
    descLineOne: 'Delivery: instant for Wise-to-Wise transfers',
    descLineTwo: 'Processing fee: $0 in USD',
    value: BillingPublisherPaymentMethod.WISE_ACCOUNT,
    isComingSoon: false,
  },
  {
    key: 'bankAccount',
    label: 'Bank transfer',
    descLineOne: 'Local (ACH / Fedwire): 1-3 business days',
    descLineTwo: 'SWIFT: 3-5 business days',
    value: BillingPublisherPaymentMethod.BANK_ACCOUNT,
    isComingSoon: false,
  },
];

function SettingsBillingDetails(): JSX.Element {
  const dispatch = useDispatch();
  const history = useHistory();

  const billingDetails = useSelector((s: StateType) => s.publisherBillings.billingDetails.data);
  const billingDetailsLs = useSelector(
    (s: StateType) => s.publisherBillings.billingDetails.loadingStatus
  );
  const billingAddress = useSelector((s: StateType) => s.publisherBillings.billingAddress.data);
  const billingAddressLs = useSelector(
    (s: StateType) => s.publisherBillings.billingAddress.loadingStatus
  );
  const { info: userProfile } = useSelector(userProfileSelector);
  const userEmail = userProfile?.email ?? '';

  const [addressForm, setAddressForm] = useState<BillingPublisherAddressType | null>(null);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  // Fields the user has interacted with — used to show validation errors as
  // soon as they leave the field (rather than only after the first save attempt).
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({});
  const billingCountries = useMemo(() => countries.filter((c) => !!c.value), []);

  // Payout-method sidebar state
  const [activeMethod, setActiveMethod] = useState<PaymentMethodOption | null>(null);
  const [paypalInitialValue, setPaypalInitialValue] = useState<
    Partial<PayPalAccountFormValue> | undefined
  >(undefined);
  const [isEditingPaypal, setIsEditingPaypal] = useState(false);
  const [wiseInitialValue, setWiseInitialValue] = useState<
    Partial<WiseAccountFormValue> | undefined
  >(undefined);
  const [isEditingWise, setIsEditingWise] = useState(false);
  // Masked summary of the currently-saved bank/Wise account, shown as a hint when
  // editing. PayPal pre-fills its initial value directly, so it doesn't need this.
  const [existingSummary, setExistingSummary] = useState<string | null>(null);
  const [bankAccountApiFieldErrors, setBankAccountApiFieldErrors] = useState<
    PayoutFieldErrorType[]
  >([]);
  const [bankAccountApiError, setBankAccountApiError] = useState<string | undefined>(undefined);
  const [wiseAccountApiFieldErrors, setWiseAccountApiFieldErrors] = useState<
    PayoutFieldErrorType[]
  >([]);
  const [wiseAccountApiError, setWiseAccountApiError] = useState<string | undefined>(undefined);
  const [paypalApiFieldErrors, setPaypalApiFieldErrors] = useState<PayoutFieldErrorType[]>([]);
  const [paypalApiError, setPaypalApiError] = useState<string | undefined>(undefined);
  const [isSavingWithdrawal, setIsSavingWithdrawal] = useState(false);
  const [isAwaitingVerification, setIsAwaitingVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResendingCode, setIsResendingCode] = useState(false);
  // Throttle the resend button so the publisher can't spam the server. Counts
  // down from 30s after each send. `resentAt` drives a short-lived "Code sent"
  // confirmation so the user gets feedback that the resend actually fired.
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resentAt, setResentAt] = useState<number | null>(null);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setTimeout(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const isIndividual =
    addressForm?.payoutProfileType !== BillingPublisherPayoutProfileType.BUSINESS;

  // Country-specific localisation for the Tax information fields, driven by the publisher's
  // country of tax residence (the country whose tax authority issues the ID we collect).
  const countryTaxInfo = useMemo(
    () => getCountryTaxInfo(addressForm?.countryOfTaxResidence),
    [addressForm?.countryOfTaxResidence]
  );
  const foreignTaxIdLocalName = isIndividual
    ? countryTaxInfo?.individualLabel
    : countryTaxInfo?.businessLabel;
  const foreignTaxIdExample = isIndividual
    ? countryTaxInfo?.individualExample
    : countryTaxInfo?.businessExample;
  const vatLocalName = countryTaxInfo?.vatLabel;
  const vatExample = countryTaxInfo?.vatExample;

  // True once the publisher has chosen the United States as their country of tax residence.
  // US-resident publishers file a W-9, which omits W-8 specific fields (foreign TIN,
  // citizenship, incorporation, VAT), so we hide them entirely instead of just optionalising.
  const isUsTaxResident = isUsCountry(addressForm?.countryOfTaxResidence);

  // IRS form the publisher will be issued: W-9 for US tax residents (individuals and
  // businesses both file W-9), W-8BEN for non-US individuals, W-8BEN-E for non-US entities.
  // Empty country yet → fall back to a generic prompt.
  const taxFormCopy = useMemo(() => {
    const country = addressForm?.countryOfTaxResidence;
    if (!country?.trim()) {
      return {
        formName: '',
        hint: 'We’ll use this info to prepare your tax form. Shared only with our accounting team.',
      };
    }
    if (isUsCountry(country)) {
      return {
        formName: 'W-9',
        hint: 'We’ll use this info to prepare your W-9 form for U.S. tax reporting. Shared only with our accounting team.',
      };
    }
    if (isIndividual) {
      return {
        formName: 'W-8BEN',
        hint: 'We’ll use this info to prepare your W-8BEN form, required for U.S. tax reporting of non-U.S. residents. Shared only with our accounting team.',
      };
    }
    return {
      formName: 'W-8BEN-E',
      hint: 'We’ll use this info to prepare your W-8BEN-E form, required for U.S. tax reporting of non-U.S. entities. Shared only with our accounting team.',
    };
  }, [addressForm?.countryOfTaxResidence, isIndividual]);

  const billingErrors = useMemo(
    () => validateBillingProfile(addressForm, isIndividual),
    [addressForm, isIndividual]
  );
  const isAddressValid = Object.keys(billingErrors).length === 0;
  // Surface an error as soon as the user leaves an invalid field (touched),
  // and again when they submit. Both feel right but for different reasons:
  // blur-based feedback catches typos early, while showErrors guarantees the
  // user sees every missing field at submit time.
  const errorFor = (field: string): string | undefined => {
    if (!showErrors && !touchedFields.has(field)) return undefined;
    return billingErrors[field];
  };
  const markTouched = useCallback((field: string) => {
    setTouchedFields((prev) => {
      if (prev.has(field)) return prev;
      const next = new Set(prev);
      next.add(field);
      return next;
    });
  }, []);
  const registerFieldRef = useCallback(
    (field: string) =>
      (node: HTMLElement | null): void => {
        fieldRefs.current[field] = node;
      },
    []
  );

  // Tax-info fields whose presence determines whether the publisher's W-8 form can be issued.
  // We surface this separately from generic billing errors so the gate message can point the
  // publisher to the right section instead of a generic "complete your profile" prompt.
  const TAX_INFO_FIELDS = [
    'countryOfTaxResidence',
    'countryOfCitizenship',
    'countryOfIncorporation',
    'entityClassification',
    'foreignTin',
    'usTin',
  ];
  const hasTaxInfoErrors = TAX_INFO_FIELDS.some((f) => billingErrors[f]);

  const isPayoutUserReady = !!billingAddress?.addressId;

  const hasUnsavedAddressChanges = useMemo(() => {
    if (!addressForm || !billingAddress) return false;
    const fields: (keyof BillingPublisherAddressType)[] = [
      'firstLine',
      'secondLine',
      'city',
      'state',
      'zipCode',
      'country',
      'contactName',
      'contactEmail',
      'companyName',
      'dateOfBirth',
      'payoutProfileType',
      'tax',
      'foreignTin',
      'usTin',
      'countryOfTaxResidence',
      'countryOfCitizenship',
      'countryOfIncorporation',
      'entityClassification',
    ];
    return fields.some((f) => (addressForm[f] ?? '') !== (billingAddress[f] ?? ''));
  }, [addressForm, billingAddress]);

  const isAddressSaved = isPayoutUserReady && !hasUnsavedAddressChanges;

  useEffect(() => {
    dispatch(fetchPublisherBillingDetails());
    dispatch(fetchBillingAddress());
  }, [dispatch]);

  useEffect(() => {
    if (billingAddress) {
      setAddressForm(billingAddress);
    }
  }, [billingAddress]);

  const paymentSettingsList = billingDetails?.paymentSettingsList ?? [];
  const connectedMethods = useMemo(
    () => new Set(paymentSettingsList.map((s) => s.paymentMethod)),
    [paymentSettingsList]
  );
  const selectableMethods = useMemo(
    () =>
      paymentMethods.filter((m) => {
        // Direct Wise-to-Wise withdrawal is temporarily hidden (not removed) until
        // all cases are aligned with Wise Support. Bank transfer (by Wise) stays
        // enabled. Publishers with an already-connected Wise account still see it
        // in their payout methods list above.
        if (
          m.value === BillingPublisherPaymentMethod.WISE_ACCOUNT &&
          !isFeatureEnabled('ENABLE_DIRECT_WISE_WITHDRAWAL', { includeDev: false })
        ) {
          return false;
        }
        return m.isComingSoon || !connectedMethods.has(m.value as PaymentMethodValue);
      }),
    [connectedMethods]
  );

  const scrollToFirstError = (errors: Record<string, string>) => {
    const firstField = Object.keys(errors)[0];
    if (!firstField) return;
    const node = fieldRefs.current[firstField];
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const focusable = node.querySelector<HTMLElement>('input, select, textarea, button');
      focusable?.focus({ preventScroll: true });
    }
  };

  const onSaveAddress = async () => {
    if (!addressForm || isSavingAddress) return;

    if (!isAddressValid) {
      setShowErrors(true);
      scrollToFirstError(billingErrors);
      return;
    }

    try {
      setIsSavingAddress(true);
      const upper = (v: string | undefined | null) => (v ?? '').toUpperCase();
      await dispatch(
        updatePublisherBillingAddress(
          {
            ...addressForm,
            country: upper(addressForm.country),
            countryOfTaxResidence: upper(addressForm.countryOfTaxResidence),
            countryOfCitizenship: upper(addressForm.countryOfCitizenship),
            countryOfIncorporation: upper(addressForm.countryOfIncorporation),
            foreignTin: (addressForm.foreignTin ?? '').trim(),
            usTin: (addressForm.usTin ?? '').trim(),
          },
          () => {
            setIsSavingAddress(false);
            setShowErrors(false);
          }
        )
      );
    } catch (error) {
      setIsSavingAddress(false);
      dispatch(
        addNotification({
          title: parseApiErrorMessage(error) || 'Failed to save billing details',
          type: 'error',
        })
      );
    }
  };

  const resetSidebarState = useCallback(() => {
    setPaypalInitialValue(undefined);
    setIsEditingPaypal(false);
    setWiseInitialValue(undefined);
    setIsEditingWise(false);
    setExistingSummary(null);
    setVerificationCode('');
    setIsAwaitingVerification(false);
    setIsSavingWithdrawal(false);
    setIsResendingCode(false);
    setResendCooldown(0);
    setResentAt(null);
    setBankAccountApiFieldErrors([]);
    setBankAccountApiError(undefined);
    setWiseAccountApiFieldErrors([]);
    setWiseAccountApiError(undefined);
    setPaypalApiFieldErrors([]);
    setPaypalApiError(undefined);
  }, []);

  const openSidebarFor = (
    option: PaymentMethodOption,
    existing?: BillingPublisherPayoutSettingsType,
    options: { awaitVerification?: boolean } = {}
  ) => {
    if (!isAddressValid) {
      setShowErrors(true);
      dispatch(
        addNotification({
          title: hasTaxInfoErrors
            ? 'Complete your tax information before adding a payout method.'
            : 'Complete your billing profile before adding a payout method.',
          type: 'warning',
        })
      );
      return;
    }
    if (!isAddressSaved) {
      dispatch(
        addNotification({
          title: hasUnsavedAddressChanges
            ? 'Save your billing changes before adding or editing payout methods.'
            : 'Save your billing details before adding or editing payout methods.',
          type: 'warning',
        })
      );
      return;
    }
    if (option.isComingSoon) return;

    resetSidebarState();
    setActiveMethod(option);
    setIsAwaitingVerification(!!options.awaitVerification);

    if (option.value === BillingPublisherPaymentMethod.PAYPAL) {
      const existingPayPal: BillingPublisherPaymentPayPalSettingsType | undefined =
        existing?.payPal;
      setPaypalInitialValue({
        email: existingPayPal?.email ?? '',
        transferMethodCountry: (
          existingPayPal?.transferMethodCountry ||
          billingAddress?.country ||
          ''
        ).toUpperCase(),
        transferMethodCurrency: existingPayPal?.transferMethodCurrency ?? '',
      });
      setIsEditingPaypal(!!existingPayPal);
    } else if (option.value === BillingPublisherPaymentMethod.BANK_ACCOUNT) {
      setExistingSummary(existing?.bankAccount?.accountSummary ?? null);
    } else if (option.value === BillingPublisherPaymentMethod.WISE_ACCOUNT) {
      const existingWise: BillingPublisherPaymentWiseAccountSettingsType | undefined =
        existing?.wiseAccount;
      // `email` and `account_holder_name` are write-only on the server side;
      // there is nothing to pre-fill on edit, so we always start from an empty
      // form and surface the masked summary as a hint instead.
      setIsEditingWise(!!existingWise);
      setExistingSummary(existingWise?.accountSummary ?? null);
    }
  };

  const closeSidebar = () => {
    resetSidebarState();
    setActiveMethod(null);
  };

  const RESEND_COOLDOWN_SECONDS = 30;
  // Server validates the code case-insensitively but matches against the issued
  // value, so we normalise to uppercase as the user types.
  const normalizedVerificationCode = verificationCode.trim().toUpperCase();
  const isValidVerificationCode = useMemo(
    () => /^[A-Z0-9]{6}$/.test(normalizedVerificationCode),
    [normalizedVerificationCode]
  );

  // Called right after a verification email has been (re)sent. Resets the
  // throttle so the publisher can't spam Resend, and shows a brief "Code sent"
  // confirmation in the verification panel.
  const onVerificationSent = useCallback(() => {
    setVerificationCode('');
    setIsAwaitingVerification(true);
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    setResentAt(Date.now());
  }, []);

  const backFromVerification = () => {
    setIsAwaitingVerification(false);
    setVerificationCode('');
    setResendCooldown(0);
    setResentAt(null);
  };

  const onSavePayPal = async (value: PayPalAccountFormValue): Promise<void> => {
    if (isSavingWithdrawal) return;
    try {
      setIsSavingWithdrawal(true);
      setPaypalApiFieldErrors([]);
      setPaypalApiError(undefined);
      const response = await dispatch<any>(
        updatePublisherPayoutSettings({
          method: BillingPublisherPaymentMethod.PAYPAL,
          email: value.email,
          transferMethodCountry: value.transferMethodCountry,
          transferMethodCurrency: value.transferMethodCurrency,
        })
      );
      if (response?.isSuccess) {
        onVerificationSent();
      } else if (response) {
        setPaypalApiFieldErrors(response.fieldErrorsList ?? []);
        if (!response.fieldErrorsList?.length && response.message) {
          setPaypalApiError(response.message);
        }
      }
    } catch (error) {
      dispatch(
        addNotification({
          title: parseApiErrorMessage(error) || 'Failed to send verification code',
          type: 'error',
        })
      );
    } finally {
      setIsSavingWithdrawal(false);
    }
  };

  const onVerifyCode = async () => {
    if (!isValidVerificationCode || isVerifying || !activeMethod) return;
    try {
      setIsVerifying(true);
      await dispatch<any>(
        verifyPublisherPayoutValidationCode(
          normalizedVerificationCode,
          activeMethod.value as PaymentMethodValue,
          () => closeSidebar()
        )
      );
    } catch (error) {
      dispatch(
        addNotification({
          title: parseApiErrorMessage(error) || 'Verification failed. Please try again.',
          type: 'error',
        })
      );
    } finally {
      setIsVerifying(false);
    }
  };

  const onResendCode = async () => {
    if (isResendingCode || resendCooldown > 0) return;
    try {
      setIsResendingCode(true);
      await dispatch<any>(resendPublisherPayoutValidationCode());
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setResentAt(Date.now());
    } catch (error) {
      dispatch(
        addNotification({
          title: parseApiErrorMessage(error) || 'Failed to resend verification code.',
          type: 'error',
        })
      );
    } finally {
      setIsResendingCode(false);
    }
  };

  const onRemovePayoutMethod = (method: PaymentMethodValue) => {
    dispatch<any>(removePublisherPayoutSettings(method)).catch(() => {});
  };

  const handleCloseForm = () => {
    if (hasUnsavedAddressChanges) {
      const ok = window.confirm('You have unsaved billing changes. Leave without saving?');
      if (!ok) return;
    }
    history.goBack();
  };

  const renderSidebarIcon = () => {
    if (!activeMethod) return null;
    switch (activeMethod.value) {
      case BillingPublisherPaymentMethod.PAYPAL:
        return <img src={payPalImg} alt="" aria-hidden="true" />;
      case BillingPublisherPaymentMethod.BANK_ACCOUNT:
        return <img src={directDepositImg} alt="" aria-hidden="true" />;
      case BillingPublisherPaymentMethod.WISE_ACCOUNT:
        return <img src={wiseImg} alt="" aria-hidden="true" />;
      default:
        return null;
    }
  };

  const onSaveBankAccount = async (value: BankAccountFormValue): Promise<void> => {
    if (isSavingWithdrawal) return;
    try {
      setIsSavingWithdrawal(true);
      setBankAccountApiFieldErrors([]);
      setBankAccountApiError(undefined);
      const response = await dispatch<any>(
        updatePublisherPayoutSettings({
          method: BillingPublisherPaymentMethod.BANK_ACCOUNT,
          ...value,
        })
      );
      if (response?.isSuccess) {
        onVerificationSent();
      } else if (response) {
        setBankAccountApiFieldErrors(response.fieldErrorsList ?? []);
        if (!response.fieldErrorsList?.length && response.message) {
          setBankAccountApiError(response.message);
        }
      }
    } catch (error) {
      dispatch(
        addNotification({
          title: parseApiErrorMessage(error) || 'Failed to save bank account',
          type: 'error',
        })
      );
    } finally {
      setIsSavingWithdrawal(false);
    }
  };

  const onSaveWiseAccount = async (value: WiseAccountFormValue): Promise<void> => {
    if (isSavingWithdrawal) return;
    try {
      setIsSavingWithdrawal(true);
      setWiseAccountApiFieldErrors([]);
      setWiseAccountApiError(undefined);
      const response = await dispatch<any>(
        updatePublisherPayoutSettings({
          method: BillingPublisherPaymentMethod.WISE_ACCOUNT,
          ...value,
        })
      );
      if (response?.isSuccess) {
        onVerificationSent();
      } else if (response) {
        setWiseAccountApiFieldErrors(response.fieldErrorsList ?? []);
        if (!response.fieldErrorsList?.length && response.message) {
          setWiseAccountApiError(response.message);
        }
      }
    } catch (error) {
      dispatch(
        addNotification({
          title: parseApiErrorMessage(error) || 'Failed to save Wise account',
          type: 'error',
        })
      );
    } finally {
      setIsSavingWithdrawal(false);
    }
  };

  const renderVerificationStep = () => {
    if (!activeMethod) return null;
    // The back-end emails the code to the Respona account that owns the
    // publisher organization, never to the payout account (PayPal / Wise /
    // bank). Showing the exact address lets the publisher know where to look
    // and makes it obvious the payout email is not involved.
    const sentTo = userEmail ? ` to ${userEmail}` : ' to your Respona account email';
    const description = (() => {
      switch (activeMethod.value) {
        case BillingPublisherPaymentMethod.PAYPAL:
          return `Enter the 6-character verification code we just sent${sentTo} to confirm your PayPal account.`;
        case BillingPublisherPaymentMethod.WISE_ACCOUNT:
          return `Enter the 6-character verification code we just sent${sentTo} to confirm your Wise account.`;
        case BillingPublisherPaymentMethod.BANK_ACCOUNT:
        default:
          return `Enter the 6-character verification code we just sent${sentTo} to confirm your bank account.`;
      }
    })();
    const changeAccountLabel = (() => {
      switch (activeMethod.value) {
        case BillingPublisherPaymentMethod.PAYPAL:
          return 'Change PayPal details';
        case BillingPublisherPaymentMethod.WISE_ACCOUNT:
          return 'Change Wise details';
        case BillingPublisherPaymentMethod.BANK_ACCOUNT:
        default:
          return 'Change bank details';
      }
    })();
    // Show a brief confirmation right after a (re)send so the user knows the
    // resend actually fired. Two seconds is enough to register, short enough
    // that it doesn't linger.
    const showResentToast = resentAt != null && Date.now() - resentAt < 2000;

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onVerifyCode();
        }}
        noValidate
        data-testid="publisher-billing-verify"
      >
        <div className="payment-details-description-wrapper">
          <p className="payment-details-description">{description}</p>

          <div className="payment-details-input-container">
            <label className="input-label" htmlFor="payout-verification-code">
              Verification code
            </label>
            <Input
              id="payout-verification-code"
              placeholder="6-character code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.toUpperCase())}
              maxLength={6}
              autoFocus
              inputMode="text"
              autoComplete="one-time-code"
              spellCheck={false}
              data-testid="publisher-billing-verify-code"
            />
          </div>
          <div className="payment-details-input-container">
            <Button
              className="connect-button"
              size="l"
              type="primary"
              onClick={onVerifyCode}
              disabled={!isValidVerificationCode || isVerifying}
              isLoading={isVerifying}
              data-testid="publisher-billing-verify-submit"
            >
              Verify
            </Button>
          </div>
          <div className="payment-details-resend">
            <span className="payment-details-resend__hint">Didn’t get a code?</span>
            <button
              type="button"
              className="payment-details-resend__btn"
              onClick={onResendCode}
              disabled={isResendingCode || isVerifying || resendCooldown > 0}
              data-testid="publisher-billing-verify-resend"
              data-disabled={isResendingCode || isVerifying || resendCooldown > 0}
            >
              {isResendingCode
                ? 'Resending…'
                : resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : 'Resend code'}
            </button>
            {showResentToast && (
              <span
                className="payment-details-resend__sent"
                role="status"
                data-testid="publisher-billing-verify-sent"
              >
                Code sent ✓
              </span>
            )}
          </div>
          <div className="payment-details-back">
            <button
              type="button"
              className="payment-details-back__btn"
              onClick={backFromVerification}
              disabled={isVerifying}
              data-testid="publisher-billing-verify-back"
            >
              ← {changeAccountLabel}
            </button>
          </div>
        </div>
      </form>
    );
  };

  const renderSidebarBody = () => {
    if (!activeMethod) return null;

    if (isAwaitingVerification) {
      return renderVerificationStep();
    }

    if (activeMethod.value === BillingPublisherPaymentMethod.BANK_ACCOUNT) {
      const holderName = isIndividual
        ? (addressForm?.contactName ?? '')
        : (addressForm?.companyName ?? '');
      return (
        <BankAccountForm
          initialCountry={billingAddress?.country?.toUpperCase() ?? ''}
          initialLegalType={isIndividual ? LEGAL_TYPES.PRIVATE : LEGAL_TYPES.BUSINESS}
          initialAccountHolderName={holderName}
          defaultBillingAddress={{
            addressLine1: billingAddress?.firstLine ?? '',
            city: billingAddress?.city ?? '',
            stateProvince: billingAddress?.state ?? '',
            postalCode: billingAddress?.zipCode ?? '',
            country: billingAddress?.country?.toUpperCase() ?? '',
          }}
          onSubmit={onSaveBankAccount}
          onCancel={closeSidebar}
          isSubmitting={isSavingWithdrawal}
          apiError={bankAccountApiError}
          apiFieldErrors={bankAccountApiFieldErrors}
        />
      );
    }

    if (activeMethod.value === BillingPublisherPaymentMethod.PAYPAL) {
      return (
        <PayPalAccountForm
          initialValue={paypalInitialValue}
          defaultCountry={billingAddress?.country?.toUpperCase() ?? ''}
          onSubmit={onSavePayPal}
          onCancel={closeSidebar}
          isSubmitting={isSavingWithdrawal}
          isEditing={isEditingPaypal}
          apiError={paypalApiError}
          apiFieldErrors={paypalApiFieldErrors}
        />
      );
    }

    if (activeMethod.value === BillingPublisherPaymentMethod.WISE_ACCOUNT) {
      return (
        <WiseAccountForm
          initialValue={wiseInitialValue}
          onSubmit={onSaveWiseAccount}
          onCancel={closeSidebar}
          isSubmitting={isSavingWithdrawal}
          isEditing={isEditingWise}
          apiError={wiseAccountApiError}
          apiFieldErrors={wiseAccountApiFieldErrors}
        />
      );
    }

    return null;
  };

  return (
    <div className="page-content">
      <PageHeader
        title="Billing details"
        renderHeaderActions={() => (
          <button
            type="button"
            className="change-billing-form__close-btn"
            onClick={handleCloseForm}
            aria-label="Close billing details"
          >
            <SVGIcon icon="crossBlack" />
          </button>
        )}
      />
      {billingAddressLs === loadingStatuses.INITED ||
      billingDetailsLs === loadingStatuses.INITED ? (
        <Loader />
      ) : (
        <div className="publisher-change-billing-form" data-testid="publisher-billing-details">
          <div className="change-billing-form">
            <form
              className="change-billing-form__address"
              onSubmit={(e) => {
                e.preventDefault();
                onSaveAddress();
              }}
              noValidate
            >
              {/* Account type */}
              <div className="change-billing-form__title change-billing-form__title--first">
                Account type
              </div>
              <div className="change-billing-form__profile-toggle">
                <Radio
                  value={BillingPublisherPayoutProfileType.INDIVIDUAL}
                  isChecked={isIndividual}
                  onChange={() =>
                    setAddressForm((prev) => ({
                      ...prev!,
                      payoutProfileType: BillingPublisherPayoutProfileType.INDIVIDUAL,
                    }))
                  }
                >
                  Individual
                </Radio>
                <Radio
                  value={BillingPublisherPayoutProfileType.BUSINESS}
                  isChecked={!isIndividual}
                  onChange={() =>
                    setAddressForm((prev) => ({
                      ...prev!,
                      payoutProfileType: BillingPublisherPayoutProfileType.BUSINESS,
                    }))
                  }
                >
                  Business
                </Radio>
              </div>

              {isIndividual ? (
                <div className="change-billing-form__address-grid change-billing-form__address-grid--individual">
                  <BillingFormField
                    id="billing-full-name"
                    value={addressForm?.contactName}
                    label="Full legal name"
                    placeholder="First and last name"
                    onChange={(v: string) =>
                      setAddressForm((prev) => ({ ...prev!, contactName: v }))
                    }
                    onBlur={() => markTouched('contactName')}
                    isError={!!errorFor('contactName')}
                    errorMessage={errorFor('contactName')}
                    maxLength={PAYOUT_MAX.contactName}
                    className="change-billing-form__full-name"
                    testId="publisher-billing-address-field-contactName"
                  />
                  <BillingFormField
                    label="Date of birth"
                    isError={!!errorFor('dateOfBirth')}
                    errorMessage={errorFor('dateOfBirth')}
                    hint="You must be 18 or older."
                    className="change-billing-form__dob"
                    testId="publisher-billing-address-field-dateOfBirth"
                  >
                    <input
                      type="date"
                      className="billing-dob-input"
                      aria-label="Date of birth"
                      max={DOB_MAX_ISO}
                      min={DOB_MIN_ISO}
                      value={
                        addressForm?.dateOfBirth
                          ? new Date(addressForm.dateOfBirth).toISOString().split('T')[0]
                          : ''
                      }
                      onChange={(e) => {
                        const ts = e.target.value ? new Date(e.target.value).getTime() : 0;
                        setAddressForm((prev) => ({ ...prev!, dateOfBirth: ts }));
                      }}
                      onBlur={() => markTouched('dateOfBirth')}
                    />
                  </BillingFormField>
                  <BillingFormField
                    id="billing-individual-email"
                    value={addressForm?.contactEmail}
                    label="Email address"
                    placeholder="Email address"
                    onChange={(v: string) =>
                      setAddressForm((prev) => ({ ...prev!, contactEmail: v }))
                    }
                    onBlur={() => markTouched('contactEmail')}
                    isError={!!errorFor('contactEmail')}
                    errorMessage={errorFor('contactEmail')}
                    maxLength={PAYOUT_MAX.email}
                    className="change-billing-form__email"
                    testId="publisher-billing-address-field-contactEmail"
                  />
                </div>
              ) : (
                <div className="change-billing-form__address-grid change-billing-form__address-grid--business">
                  <BillingFormField
                    id="billing-company-name"
                    value={addressForm?.companyName}
                    label="Company name"
                    placeholder="Company name"
                    onChange={(v: string) =>
                      setAddressForm((prev) => ({ ...prev!, companyName: v }))
                    }
                    onBlur={() => markTouched('companyName')}
                    isError={!!errorFor('companyName')}
                    errorMessage={errorFor('companyName')}
                    maxLength={PAYOUT_MAX.businessName}
                    className="change-billing-form__company"
                    testId="publisher-billing-address-field-companyName"
                  />
                  <BillingFormField
                    id="billing-contact-name"
                    value={addressForm?.contactName}
                    label="Contact name (optional)"
                    placeholder="First and last name"
                    onChange={(v: string) =>
                      setAddressForm((prev) => ({ ...prev!, contactName: v }))
                    }
                    maxLength={PAYOUT_MAX.contactName}
                    className="change-billing-form__contact-name"
                    testId="publisher-billing-address-field-contactName"
                  />
                  <BillingFormField
                    id="billing-business-email"
                    value={addressForm?.contactEmail}
                    label="Email address"
                    placeholder="Email address"
                    onChange={(v: string) =>
                      setAddressForm((prev) => ({ ...prev!, contactEmail: v }))
                    }
                    onBlur={() => markTouched('contactEmail')}
                    isError={!!errorFor('contactEmail')}
                    errorMessage={errorFor('contactEmail')}
                    maxLength={PAYOUT_MAX.email}
                    className="change-billing-form__email"
                    testId="publisher-billing-address-field-contactEmail"
                  />
                </div>
              )}

              {/* Billing address */}
              <div className="change-billing-form__title">Billing address</div>
              <div className="change-billing-form__address-grid">
                <BillingFormField
                  id="billing-address-line-1"
                  value={addressForm?.firstLine}
                  label="Address Line 1"
                  placeholder="Address Line 1"
                  onChange={(value: string) =>
                    setAddressForm((prev) => ({ ...prev!, firstLine: value }))
                  }
                  onBlur={() => markTouched('firstLine')}
                  isError={!!errorFor('firstLine')}
                  errorMessage={errorFor('firstLine')}
                  maxLength={PAYOUT_MAX.addressLine}
                  className="change-billing-form__addressLine1"
                  testId="publisher-billing-address-field-firstLine"
                />
                <BillingFormField
                  id="billing-address-line-2"
                  value={addressForm?.secondLine}
                  label="Address Line 2"
                  placeholder="Address Line 2"
                  onChange={(value: string) =>
                    setAddressForm((prev) => ({ ...prev!, secondLine: value }))
                  }
                  onBlur={() => markTouched('secondLine')}
                  isError={!!errorFor('secondLine')}
                  errorMessage={errorFor('secondLine')}
                  maxLength={PAYOUT_MAX.addressLine}
                  className="change-billing-form__addressLine2"
                  testId="publisher-billing-address-field-secondLine"
                />
                <BillingFormField
                  id="billing-city"
                  value={addressForm?.city}
                  label="City"
                  placeholder="City"
                  onChange={(value: string) =>
                    setAddressForm((prev) => ({ ...prev!, city: value }))
                  }
                  onBlur={() => markTouched('city')}
                  isError={!!errorFor('city')}
                  errorMessage={errorFor('city')}
                  maxLength={PAYOUT_MAX.city}
                  className="change-billing-form__city"
                  testId="publisher-billing-address-field-city"
                />
                <BillingFormField
                  id="billing-zip"
                  value={addressForm?.zipCode}
                  label="Zip/Postal code"
                  placeholder="Zip/Postal code"
                  onChange={(value: string) =>
                    setAddressForm((prev) => ({ ...prev!, zipCode: value }))
                  }
                  onBlur={() => markTouched('zipCode')}
                  isError={!!errorFor('zipCode')}
                  errorMessage={errorFor('zipCode')}
                  maxLength={PAYOUT_MAX.postalCode}
                  className="change-billing-form__zip-code"
                  testId="publisher-billing-address-field-zipCode"
                />
                {isUsCountry(addressForm?.country) ? (
                  <BillingFormField
                    label="State"
                    isError={!!errorFor('state')}
                    errorMessage={errorFor('state')}
                    className="change-billing-form__state"
                    testId="publisher-billing-address-field-state"
                  >
                    <Select
                      options={US_STATES}
                      value={
                        US_STATES.find(
                          (item) => item.value === (addressForm?.state ?? '').toUpperCase()
                        ) || null
                      }
                      onChange={(val) =>
                        setAddressForm((prev) => ({ ...prev!, state: val?.value || '' }))
                      }
                      placeholder="Select state"
                      menuPlacement="auto"
                    />
                  </BillingFormField>
                ) : (
                  <BillingFormField
                    id="billing-state-province"
                    value={addressForm?.state}
                    label="State / Province"
                    placeholder="State or province"
                    onChange={(value) => setAddressForm((prev) => ({ ...prev!, state: value }))}
                    onBlur={() => markTouched('state')}
                    isError={!!errorFor('state')}
                    errorMessage={errorFor('state')}
                    maxLength={PAYOUT_MAX.stateProvince}
                    className="change-billing-form__state"
                    testId="publisher-billing-address-field-state"
                  />
                )}
                <BillingFormField
                  label="Country"
                  isError={!!errorFor('country')}
                  errorMessage={errorFor('country')}
                  className="change-billing-form__country"
                  testId="publisher-billing-address-field-country"
                >
                  <Select
                    options={billingCountries}
                    value={
                      billingCountries.find(
                        (item) => item.value.toLowerCase() === addressForm?.country?.toLowerCase()
                      ) || null
                    }
                    onChange={(val) =>
                      setAddressForm((prev) => {
                        const nextCountry = val?.value || '';
                        const stateBecomesIncompatible =
                          isUsCountry(prev?.country) !== isUsCountry(nextCountry);
                        return {
                          ...prev!,
                          country: nextCountry,
                          state: stateBecomesIncompatible ? '' : (prev?.state ?? ''),
                        };
                      })
                    }
                    placeholder="Select country"
                    menuPlacement="auto"
                  />
                </BillingFormField>
              </div>

              {/* Tax information — drives W-9 (US residents) or W-8BEN / W-8BEN-E (non-US). */}
              <div className="change-billing-form__title with-hint">
                Tax information
                {taxFormCopy.formName && (
                  <span className="change-billing-form__title-form-name">
                    {' '}
                    ({taxFormCopy.formName})
                  </span>
                )}
              </div>
              <div className="change-billing-form__section-hint">{taxFormCopy.hint}</div>
              <div
                className={cn('change-billing-form__address-grid', {
                  'change-billing-form__address-grid--tax-individual':
                    isIndividual && !isUsTaxResident,
                  'change-billing-form__address-grid--tax-business':
                    !isIndividual && !isUsTaxResident,
                  'change-billing-form__address-grid--tax-us-individual':
                    isIndividual && isUsTaxResident,
                  'change-billing-form__address-grid--tax-us-business':
                    !isIndividual && isUsTaxResident,
                })}
              >
                <BillingFormField
                  label="Country of tax residence"
                  isError={!!errorFor('countryOfTaxResidence')}
                  errorMessage={errorFor('countryOfTaxResidence')}
                  className="change-billing-form__tax-residence"
                  testId="publisher-billing-address-field-countryOfTaxResidence"
                >
                  <Select
                    options={billingCountries}
                    value={
                      billingCountries.find(
                        (item) =>
                          item.value.toLowerCase() ===
                          (addressForm?.countryOfTaxResidence ?? '').toLowerCase()
                      ) || null
                    }
                    onChange={(val) =>
                      setAddressForm((prev) => {
                        const nextCountry = val?.value || '';
                        // Switching between US and non-US residence flips which
                        // IRS form applies (W-9 vs W-8BEN/E) and which fields are
                        // visible. Clear the fields that just disappeared so we
                        // don't silently keep stale values that would still be
                        // submitted on save.
                        const wasUs = isUsCountry(prev?.countryOfTaxResidence);
                        const isUs = isUsCountry(nextCountry);
                        if (wasUs === isUs) {
                          return { ...prev!, countryOfTaxResidence: nextCountry };
                        }
                        return {
                          ...prev!,
                          countryOfTaxResidence: nextCountry,
                          countryOfCitizenship: '',
                          countryOfIncorporation: '',
                          foreignTin: '',
                          tax: isUs ? '' : (prev?.tax ?? ''),
                        };
                      })
                    }
                    placeholder="Select country"
                    menuPlacement="auto"
                  />
                </BillingFormField>

                {isIndividual ? (
                  // Country of citizenship is a W-8BEN field — hidden when filing W-9.
                  !isUsTaxResident && (
                    <BillingFormField
                      label="Country of citizenship"
                      isError={!!errorFor('countryOfCitizenship')}
                      errorMessage={errorFor('countryOfCitizenship')}
                      className="change-billing-form__citizenship"
                      testId="publisher-billing-address-field-countryOfCitizenship"
                    >
                      <Select
                        options={billingCountries}
                        value={
                          billingCountries.find(
                            (item) =>
                              item.value.toLowerCase() ===
                              (addressForm?.countryOfCitizenship ?? '').toLowerCase()
                          ) || null
                        }
                        onChange={(val) =>
                          setAddressForm((prev) => ({
                            ...prev!,
                            countryOfCitizenship: val?.value || '',
                          }))
                        }
                        placeholder="Select country"
                        menuPlacement="auto"
                      />
                    </BillingFormField>
                  )
                ) : (
                  <>
                    {/* Country of incorporation is a W-8BEN-E field — hidden when filing W-9. */}
                    {!isUsTaxResident && (
                      <BillingFormField
                        label="Country of incorporation"
                        isError={!!errorFor('countryOfIncorporation')}
                        errorMessage={errorFor('countryOfIncorporation')}
                        className="change-billing-form__incorporation"
                        testId="publisher-billing-address-field-countryOfIncorporation"
                      >
                        <Select
                          options={billingCountries}
                          value={
                            billingCountries.find(
                              (item) =>
                                item.value.toLowerCase() ===
                                (addressForm?.countryOfIncorporation ?? '').toLowerCase()
                            ) || null
                          }
                          onChange={(val) =>
                            setAddressForm((prev) => ({
                              ...prev!,
                              countryOfIncorporation: val?.value || '',
                            }))
                          }
                          placeholder="Select country"
                          menuPlacement="auto"
                        />
                      </BillingFormField>
                    )}
                    <BillingFormField
                      label="Entity classification"
                      isError={!!errorFor('entityClassification')}
                      errorMessage={errorFor('entityClassification')}
                      className="change-billing-form__entity-classification"
                      testId="publisher-billing-address-field-entityClassification"
                    >
                      <Select
                        options={ENTITY_CLASSIFICATION_OPTIONS}
                        value={
                          ENTITY_CLASSIFICATION_OPTIONS.find(
                            (item) => item.value === addressForm?.entityClassification
                          ) || null
                        }
                        onChange={(val) =>
                          setAddressForm((prev) => ({
                            ...prev!,
                            entityClassification:
                              (val?.value as EntityClassificationValue | undefined) ??
                              BillingEntityClassification.ENTITY_CLASSIFICATION_UNSPECIFIED,
                          }))
                        }
                        placeholder="Select classification"
                        menuPlacement="auto"
                      />
                    </BillingFormField>
                  </>
                )}

                {/* Foreign tax ID is a W-8 field — not collected on a W-9. */}
                {!isUsTaxResident && (
                  <BillingFormField
                    id="billing-foreign-tin"
                    value={addressForm?.foreignTin ?? ''}
                    label={
                      foreignTaxIdLocalName ? `${foreignTaxIdLocalName} (tax ID)` : 'Foreign tax ID'
                    }
                    placeholder={
                      foreignTaxIdExample ??
                      (isIndividual
                        ? 'Issued by your country of tax residence'
                        : 'Issued by country of incorporation')
                    }
                    hint={foreignTaxIdExample ? `Example: ${foreignTaxIdExample}` : undefined}
                    onChange={(value) =>
                      setAddressForm((prev) => ({ ...prev!, foreignTin: value }))
                    }
                    onBlur={() => markTouched('foreignTin')}
                    isError={!!errorFor('foreignTin')}
                    errorMessage={errorFor('foreignTin')}
                    maxLength={PAYOUT_MAX.foreignTin}
                    className="change-billing-form__foreign-tin"
                    testId="publisher-billing-address-field-foreignTin"
                  />
                )}
                <BillingFormField
                  id="billing-us-tin"
                  value={addressForm?.usTin ?? ''}
                  label={(() => {
                    const idLabel = isIndividual ? 'SSN or ITIN' : 'EIN';
                    return isUsTaxResident
                      ? `US tax ID (${idLabel})`
                      : `US tax ID (${idLabel}, optional)`;
                  })()}
                  placeholder={isIndividual ? '123-45-6789' : '12-3456789'}
                  hint={
                    isIndividual
                      ? 'Format: 9 digits, e.g. 123-45-6789'
                      : 'Format: 9 digits, e.g. 12-3456789'
                  }
                  onChange={(value) => setAddressForm((prev) => ({ ...prev!, usTin: value }))}
                  onBlur={() => markTouched('usTin')}
                  isError={!!errorFor('usTin')}
                  errorMessage={errorFor('usTin')}
                  maxLength={PAYOUT_MAX.usTin}
                  className="change-billing-form__us-tin"
                  testId="publisher-billing-address-field-usTin"
                />

                {/* VAT only applies to non-US businesses — the US has no VAT system. */}
                {!isIndividual && !isUsTaxResident && (
                  <BillingFormField
                    id="billing-vat"
                    value={addressForm?.tax ?? ''}
                    label={vatLocalName ? `${vatLocalName} (optional)` : 'VAT number (optional)'}
                    placeholder={vatExample ?? 'e.g. DE123456789'}
                    hint={vatExample ? `Example: ${vatExample}` : undefined}
                    onChange={(value) => setAddressForm((prev) => ({ ...prev!, tax: value }))}
                    maxLength={PAYOUT_MAX.tax}
                    className="change-billing-form__tax"
                    testId="publisher-billing-address-field-tax"
                  />
                )}
              </div>

              <Button
                type="primary"
                onClick={onSaveAddress}
                disabled={billingAddressLs === loadingStatuses.INITED || isSavingAddress}
                isLoading={isSavingAddress}
                data-testid="publisher-billing-address-save"
              >
                Save billing details
              </Button>
            </form>
            <div
              className={cn('settings-billing__payment-method', {
                'editing-payment-method': !!activeMethod,
                'settings-billing__payment-method--disabled': !isAddressSaved,
              })}
            >
              {activeMethod ? (
                <RightInfoSidebar
                  isOpen
                  onClose={closeSidebar}
                  testId="publisher-billing-payout-sidebar"
                  classNames={{
                    container: 'sidebar-crm__container',
                    header: 'sidebar-crm__header-container',
                    closeIcon: 'sidebar-crm__close-icon',
                    body: 'sidebar-crm__body',
                  }}
                  title={
                    <div>
                      {(() => {
                        if (activeMethod.key === 'bankAccount') {
                          return existingSummary
                            ? 'Replace your bank account'
                            : 'Connect a bank account';
                        }
                        if (activeMethod.key === 'wiseAccount') {
                          return existingSummary
                            ? 'Replace your Wise account'
                            : 'Connect a Wise account';
                        }
                        return isEditingPaypal
                          ? `Update your ${activeMethod.label} account`
                          : `Connect a ${activeMethod.label} account`;
                      })()}
                    </div>
                  }
                >
                  <div className="settings-billing__payment-method-content">
                    <div className="payment-container">
                      {renderSidebarIcon()}
                      <div className="payment-text">
                        <h2 className="payment-text-title">{activeMethod.label}</h2>
                        {existingSummary && !isAwaitingVerification && (
                          <p className="payment-text-details">Currently: {existingSummary}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  {renderSidebarBody()}
                </RightInfoSidebar>
              ) : (
                <>
                  {paymentSettingsList.length > 0 && (
                    <>
                      <div className="settings-billing__payment-method-header">
                        <div className="change-billing-form__title no-space">Payout methods</div>
                      </div>
                      {paymentSettingsList.map((settings) => {
                        const option = paymentMethods.find(
                          (m) => m.value === settings.paymentMethod
                        );
                        const canEdit = !!option && isAddressSaved;
                        return (
                          <PayoutMethodCard
                            key={settings.paymentMethod}
                            settings={settings}
                            testId={`publisher-billing-payout-card-${option?.key ?? settings.paymentMethod}`}
                            onEdit={canEdit ? () => openSidebarFor(option!, settings) : undefined}
                            onVerify={
                              canEdit
                                ? () =>
                                    openSidebarFor(option!, settings, { awaitVerification: true })
                                : undefined
                            }
                            onRemove={() => onRemovePayoutMethod(settings.paymentMethod)}
                          />
                        );
                      })}
                    </>
                  )}

                  {selectableMethods.length > 0 && (
                    <>
                      <div
                        className={cn('settings-billing__payment-method-header', {
                          'settings-billing__payment-method-header--with-gap':
                            paymentSettingsList.length > 0,
                        })}
                      >
                        <div className="change-billing-form__title no-space">
                          {paymentSettingsList.length > 0
                            ? 'Add another method'
                            : 'Select payout method'}
                        </div>
                      </div>
                      {selectableMethods.map((opt) => {
                        const isMethodBlocked = !opt.isComingSoon && !isAddressSaved;
                        let methodContentClass = `settings-billing__payment-method-content settings-billing__payment-method-content--hover-${opt.key}`;

                        if (opt.isComingSoon || isMethodBlocked) {
                          methodContentClass +=
                            ' settings-billing__payment-method-content--coming-soon';
                        }

                        const blockedLabel = (() => {
                          if (!isAddressValid) {
                            return hasTaxInfoErrors
                              ? 'Complete tax information first'
                              : 'Complete billing profile first';
                          }
                          if (hasUnsavedAddressChanges) return 'Save your billing changes first';
                          return 'Save your billing details first';
                        })();

                        return (
                          <div
                            key={opt.key}
                            className={methodContentClass}
                            data-testid={`publisher-billing-method-${opt.key}`}
                            data-disabled={opt.isComingSoon || isMethodBlocked}
                            onClick={() => {
                              if (!opt.isComingSoon && !isMethodBlocked) {
                                openSidebarFor(opt);
                              }
                            }}
                          >
                            {opt.isComingSoon && <div className="coming-soon">Coming soon</div>}
                            {isMethodBlocked && (
                              <div
                                className="coming-soon"
                                data-testid="publisher-billing-method-blocked"
                              >
                                {blockedLabel}
                              </div>
                            )}
                            <div className="payment-container">
                              {(() => {
                                if (opt.value === BillingPublisherPaymentMethod.BANK_ACCOUNT) {
                                  return <img src={directDepositImg} alt="" aria-hidden="true" />;
                                }
                                if (opt.value === BillingPublisherPaymentMethod.WISE_ACCOUNT) {
                                  return <img src={wiseImg} alt="" aria-hidden="true" />;
                                }
                                return <img src={payPalImg} alt="" aria-hidden="true" />;
                              })()}
                              <div className="payment-text">
                                <h2 className="payment-text-title">{opt.label}</h2>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SettingsBillingDetails;
